/** Breathing room between the header pill and the field the bubble points at. */
const GAP = 24

/** Only ever one bubble on screen, so it can be found without bookkeeping. */
const BUBBLE_CLASS = 'form-bubble'

/**
 * What the bubble hangs under. The consent box is wrapped by its label rather
 * than pointed at by one, so the message belongs below the whole statement
 * instead of below a 16px square.
 */
function anchorFor(control) {
    return control.closest('label') || control
}

/** Drop any bubble at once, with no leave transition. */
function clearBubbles(form) {
    form.querySelectorAll(`.${BUBBLE_CLASS}`).forEach((bubble) => bubble.remove())
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'))
}

/** Play the bubble out, then remove it. */
function dismissBubble(form) {
    const bubble = form.querySelector(`.${BUBBLE_CLASS}`)
    if (!bubble) return

    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'))
    bubble.classList.remove('is-open')

    // Under reduced motion the transition is off, so `transitionend` never
    // arrives and waiting for it would strand the node in the DOM.
    if (parseFloat(getComputedStyle(bubble).transitionDuration) > 0) {
        bubble.addEventListener('transitionend', () => bubble.remove(), { once: true })
    } else {
        bubble.remove()
    }
}

function showBubble(control) {
    const wrapper = control.closest('[data-field]')
    if (!wrapper) return

    const anchor = anchorFor(control)
    const bubble = document.createElement('div')

    bubble.className = BUBBLE_CLASS
    // Announced the moment it lands, which is the whole point — nothing else
    // tells a screen reader the submit was refused.
    bubble.setAttribute('role', 'alert')
    bubble.innerHTML =
        '<svg class="form-bubble__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
        '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M8 4.4v4.2M8 11.4h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg><span></span>'

    // The browser's own wording, so it stays localised and still tells an empty
    // field apart from a malformed one.
    bubble.querySelector('span').textContent = control.validationMessage

    // `[data-field]` is positioned, so it is the offset parent these measure
    // against — no page coordinates to keep in sync as the layout moves.
    bubble.style.top = `${anchor.offsetTop + anchor.offsetHeight + 8}px`

    wrapper.append(bubble)
    control.setAttribute('aria-invalid', 'true')

    // One frame in the from-state, or there is nothing to transition out of.
    requestAnimationFrame(() => bubble.classList.add('is-open'))
}

/**
 * Disable the submit button for the rest of the post. A disabled default button
 * also blocks implicit submission, so Enter in a field can't sneak a second one
 * through either.
 */
function lockSubmit(button) {
    button.dataset.label = button.textContent
    button.textContent = 'Submitting…'
    button.disabled = true
}

function unlockSubmit(button) {
    if (!button.disabled) return

    button.textContent = button.dataset.label
    button.disabled = false
}

/** Bring the field clear of the fixed header, which covers the top of the page. */
function scrollClearOfHeader(anchor, header, lenis) {
    const rect = anchor.getBoundingClientRect()
    const minTop = header.offsetHeight + GAP

    // Already sitting clear and on screen — moving the page would be a jump the
    // visitor didn't ask for.
    if (rect.top >= minTop && rect.bottom <= window.innerHeight) return

    const top = Math.max(0, window.scrollY + rect.top - minTop)

    // lenis is null exactly when reduced motion is set, so that branch is the
    // instant one by construction.
    if (lenis) lenis.scrollTo(top)
    else window.scrollTo(0, top)
}

/**
 * Client-side validation for the register form, with our own error bubble.
 *
 * The form carries `novalidate` because the native bubble cannot be kept off
 * the nav: it is browser chrome drawn outside the page's stacking context, so
 * it has no z-index and no selector, and it paints over a fixed header at any
 * scroll position. This one is an ordinary element at `z-40` against the
 * header's `z-50`, so the nav is always in front of it.
 *
 * The constraints themselves are unchanged — `required`, `type="email"` and the
 * rest still do the work, read here through the constraint validation API. The
 * PHP handler at the top of index.php re-checks everything server-side and is
 * what catches a submit if this script never runs.
 *
 * @param {import('lenis').default|null} lenis Active instance, or null under
 *   reduced motion, when the page scrolls natively.
 */
export function initFormValidation(lenis) {
    const form = document.querySelector('#register form')
    if (!form) return

    const header = document.querySelector('#main-header')
    if (!header) return

    const submitButton = form.querySelector('button[type="submit"]')

    form.addEventListener('submit', (event) => {
        clearBubbles(form)

        // `validity.valid` rather than checkValidity(), which fires an `invalid`
        // event as a side effect of being asked.
        const invalid = Array.from(form.elements).find((el) => el.willValidate && !el.validity.valid)
        if (!invalid) {
            // Locked only once the form is actually going, so a refused submit
            // leaves the button free for the corrected one.
            if (submitButton) lockSubmit(submitButton)
            return
        }

        event.preventDefault()
        showBubble(invalid)
        scrollClearOfHeader(anchorFor(invalid), header, lenis)

        // The scroll above is ours; focus() would run its own and fight it.
        invalid.focus({ preventScroll: true })
    })

    // Any edit is an answer to the message, so it goes. Both events bubble from
    // the controls, so one listener on the form covers every field.
    form.addEventListener('input', () => dismissBubble(form))
    form.addEventListener('change', () => dismissBubble(form))

    // Back from the thank-you page restores this page from the bfcache exactly
    // as it was left — locked — with nothing to ever release it.
    window.addEventListener('pageshow', (event) => {
        if (event.persisted && submitButton) unlockSubmit(submitButton)
    })
}
